
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Key, Shield, AlertTriangle } from 'lucide-react';
import { usePassword } from '@/contexts';
import { toast } from 'sonner';

interface PassKeyResetFormProps {
  onBack: () => void;
  onAuthenticated?: () => void; // Optional callback for authentication success
}

const PassKeyResetForm = ({ onBack, onAuthenticated }: PassKeyResetFormProps) => {
  const { verifySecurePassKey } = usePassword();
  const [passKey, setPassKey] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  
  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsVerifying(true);
    
    try {
      // Log for debugging
      console.log('Verifying passkey attempt');
      
      // Check for the known pass key
      const isValid = verifySecurePassKey(passKey);
      
      if (isValid) {
        // Success! Call the onAuthenticated callback if provided
        toast.success("VIP Secure Pass Key verified successfully!");
        if (onAuthenticated) {
          onAuthenticated();
        }
      } else {
        setError('Invalid VIP Secure Pass Key');
        toast.error('Invalid VIP Secure Pass Key');
      }
    } catch (err) {
      console.error('Error verifying passkey:', err);
      setError('An error occurred while verifying the pass key');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBackClick = () => {
    console.log('Back button clicked in PassKeyResetForm');
    
    // Force immediate back navigation to ensure it triggers
    setTimeout(() => {
      console.log('Executing onBack callback from PassKeyResetForm');
      onBack();
    }, 0);
  };

  return (
    <div className="space-y-6 p-4 text-left">
      <div className="space-y-1 mb-4 text-center">
        <Shield className="h-12 w-12 mx-auto text-nalc-teal mb-2" />
        <h2 className="text-2xl font-bold">VIP Secure Pass Key</h2>
        <p className="text-muted-foreground">Enter your VIP Secure Pass Key to unlock the application</p>
        <p className="text-xs mt-1 text-amber-500">This is your permanent emergency access key</p>
      </div>
      
      <form onSubmit={handleVerify} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="pass-key">Secure Pass Key</Label>
          <Input
            id="pass-key"
            value={passKey}
            onChange={(e) => setPassKey(e.target.value)}
            className="font-mono text-sm text-left"
            dir="ltr"
            placeholder="Enter your VIP Secure Pass Key"
            autoComplete="off"
            required
          />
          
          {error && (
            <div className="flex items-center gap-2 text-red-500 mt-1">
              <AlertTriangle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          )}
        </div>
        
        <div className="flex flex-col md:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleBackClick}
            className="w-full"
          >
            Back
          </Button>
          <Button
            type="submit"
            className="w-full bg-nalc-teal hover:bg-nalc-teal/90"
            disabled={isVerifying || !passKey}
          >
            <Key className="mr-2 h-4 w-4" />
            Verify Pass Key
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PassKeyResetForm;
